import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, Subject, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { MessageService } from 'primeng/api';

import { AuthService } from '../services/auth.service';

/**
 * Manejo global de errores HTTP. Muestra los errores como toast (`key: 'http-error'`)
 * y siempre relanza el error para que el componente pueda reaccionar.
 *
 * - Sin conexión (status 0): muestra un único aviso, cancela las peticiones en curso y
 *   rechaza las nuevas durante 10 s para no saturar al usuario de mensajes.
 * - 401: muestra el mensaje y cierra la sesión local.
 * - 409 `PRICE_CONFIG_VALIDITY_GAPS_DETECTED`: no muestra nada; lo maneja el formulario de precios.
 * - Resto: muestra cada elemento de `error.errors` o, si no hay, `error.message`.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private messageService = inject(MessageService);
  private authService = inject(AuthService);

  private cancelPendingRequests$ = new Subject<void>();
  /** Bloqueo temporal tras un error de conexión (ver descripción de la clase). */
  private connectionErrorShown = false;

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.connectionErrorShown) {
      return throwError(() => new Error('No hay conexión a internet'));
    }

    return next.handle(req).pipe(
      takeUntil(this.cancelPendingRequests$),
      catchError((error: HttpErrorResponse) => {
        if (error?.error instanceof ProgressEvent || error?.status === 0) {
          if (!this.connectionErrorShown) {
            this.connectionErrorShown = true;

            this.messageService.add({
              key: 'http-error',
              severity: 'error',
              summary: 'Error de Conexión',
              detail: 'No se pudo establecer conexión con el servidor, consulte con el administrador del sistema o revise su conexión',
              life: 5000,
            });

            this.cancelPendingRequests$.next();

            setTimeout(() => {
              this.connectionErrorShown = false;
            }, 10000);
          }

          return throwError(() => error);
        }

        this.connectionErrorShown = false;
        const errorMessages = this.getErrorMessages(error);

        // El error 409 PRICE_CONFIG_VALIDITY_GAPS_DETECTED lo maneja el componente con un modal
        if (
          error.status === 409 &&
          error?.error?.error_code === 'PRICE_CONFIG_VALIDITY_GAPS_DETECTED'
        ) {
          return throwError(() => error);
        }

        if (error.status === 401) {
          if (errorMessages.length > 0) {
            this.showMessages('error', errorMessages);
          } else {
            const message = error?.error?.message || error?.error || error?.message;
            this.messageService.add({
              key: 'http-error',
              severity: 'error',
              summary: 'Error de Autenticación',
              detail: message || 'Tu sesión ha expirado. Inicia sesión nuevamente.',
              life: 5000,
            });
          }

          if (this.authService.isAuthenticated()) {
            this.authService.logoutLocal();
          }

          return throwError(() => error);
        }

        if (errorMessages.length > 0) {
          this.showMessages('error', errorMessages);
        } else {
          // Si no hay array de errores, mostrar el mensaje principal
          const message = error?.error?.message || error?.error || error?.message || 'Ocurrió un error inesperado';
          this.messageService.add({
            key: 'http-error',
            severity: 'error',
            summary: `Error ${error.status || ''}`,
            detail: typeof message === 'string' ? message : JSON.stringify(message),
            life: 5000,
          });
        }

        return throwError(() => error);
      }),
    );
  }

  /** Extrae los mensajes de `error.errors`, que puede contener strings u objetos con `message`/`detail`. */
  private getErrorMessages(error: HttpErrorResponse): string[] {
    const rawErrors = error?.error?.errors;

    if (!Array.isArray(rawErrors) || rawErrors.length === 0) {
      return [];
    }

    return rawErrors
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (item?.message) {
          return item.message;
        }

        if (item?.detail) {
          return item.detail;
        }

        return null;
      })
      .filter((message): message is string => !!message);
  }

  private showMessages(severity: 'error' | 'warn', messages: string[]): void {
    const timestamp = Date.now();
    messages.forEach((message, index) => {
      this.messageService.add({
        key: 'http-error',
        severity,
        summary: severity === 'warn' ? 'Advertencia' : 'Error',
        detail: message,
        life: 5000,
      });
    });
  }
}
